import { Component, Injector, OnInit } from '@angular/core';
import { User, authState } from '@angular/fire/auth';
import { Data } from '@angular/router';
import { LANGUAGES } from '@modeso/aumico-lib-core-fe';
import {
    AcceptInvitationDto,
    GetInvitationDto,
    IPersonalInformation,
    ISetOrResetPasswordRequest,
    InvitationProvider,
    invitation,
    verifyOOBCode,
} from '@modeso/aumico-lib-users-fe';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import { ActionsSubject } from '@ngrx/store';
import { mergeMap, take, tap } from 'rxjs';
import { Paths } from '../../enums/paths.enum';
import { PasswordsBasePage } from '../passwords/passwords-base.page';

const GERMAN_PRIVACY_POLICY_URL = 'https://aumico.io/datenschutz/';
const ENGLISH_PRIVACY_POLICY_LINK = 'https://aumico.io/en/datenschutz/';
@Component({
    selector: 'app-accept-invitation',
    templateUrl: './accept-invitation.page.html',
    styleUrls: ['./accept-invitation.page.scss'],
})
@UntilDestroy()
export class AcceptInvitationPage extends PasswordsBasePage implements OnInit {
    private readonly authState$ = authState(this.auth);
    private invitationUuid: string;
    organizationName: string;
    inviteeEmail: string;
    showMetaInformationForm = true;
    personalInformationData: IPersonalInformation;
    email: string;
    isOOBCodeExpiredOrInvalid: boolean;
    errorMessage: string;
    passwordData: ISetOrResetPasswordRequest;

    // private oobCode: string;

    constructor(
        injector: Injector,
        private readonly invitationProvider: InvitationProvider,
        private readonly actionListener$: ActionsSubject,
    ) {
        super(injector);
    }
    override ngOnInit(): void {
        super.ngOnInit();
        const routeData$ = this.activatedRoute.data;
        routeData$
            .pipe(
                mergeMap((data: Data) => {
                    const invitation: GetInvitationDto = data['invitation'];

                    this.inviteeEmail = invitation.email;
                    this.organizationName = invitation.organizationName;

                    const shareExpired = this.checkIfShareExpired(invitation);
                    if (shareExpired) this.errorMessage = 'Share Expired';

                    return this.authState$;
                }),
                tap((user: User) => {
                    if (user != null && user.email === this.inviteeEmail) {
                        this.handleUserIsLoggedInWithSameEmail();
                    }
                }),
                take(1),
            )
            .subscribe(() => {
                this.invitationUuid = this.activatedRoute.snapshot.queryParams['invitation-uuid'] as string;
            });

        const currentLanguage = this.getCurrentLanguageWithFallBack();
        this.actionListener$.pipe(ofType(verifyOOBCode.success), untilDestroyed(this)).subscribe(({ payload }) => {
            if (payload !== this.inviteeEmail) {
                this.router.navigate([currentLanguage + '/organization/invitation-expired']);
                return;
            }
        });
        this.actionListener$
            .pipe(
                ofType(invitation.acceptInvitationSuccess),
                mergeMap(({ payload }) => {
                    this.authProvider.dispatchVerifyOOB({ oobCode: payload, email: this.inviteeEmail });
                    return this.actionListener$.pipe(ofType(verifyOOBCode.success));
                }),
                untilDestroyed(this),
            )
            .subscribe(({ payload }) => {
                if (payload !== this.inviteeEmail) {
                    this.isOOBCodeExpiredOrInvalid = true;
                } else {
                    this.authProvider.dispatchResetPassword(this.passwordData);
                }
            });
        this.actionListener$
            .pipe(ofType(verifyOOBCode.failure, invitation.getFailure), untilDestroyed(this))
            .subscribe(() => {
                this.router.navigate([currentLanguage + '/organization/invitation-expired']);
                return;
            });

        this.actionListener$
            .pipe(ofType(invitation.acceptInvitationFailure), untilDestroyed(this))
            .subscribe(({ payload }) => {
                this.errorMessage = payload.message;
            });
    }
    onPersonalInformationEmitted(personalInformationData: IPersonalInformation): void {
        this.personalInformationData = personalInformationData;
        this.showMetaInformationForm = false;
    }
    onPasswordReset(passwordData: ISetOrResetPasswordRequest): void {
        if (this.personalInformationData == null) {
            throw new Error('Data not complete');
        }

        const acceptInvitation: AcceptInvitationDto = this.getAcceptInvitationDto(passwordData);

        passwordData.email = this.inviteeEmail;
        this.passwordData = passwordData;
        this.invitationProvider.dispatchAcceptInvitation(this.invitationUuid, acceptInvitation, passwordData);
    }

    redirectToPrivacyPolicy(): void {
        if (this.personalInformationData == null || this.personalInformationData?.locale === LANGUAGES.ENGLISH) {
            window.open(ENGLISH_PRIVACY_POLICY_LINK);
        } else {
            window.open(GERMAN_PRIVACY_POLICY_URL);
        }
    }

    private handleUserIsLoggedInWithSameEmail() {
        const language = this.localStorageService.getCurrentLanguage();
        this.router.navigateByUrl(`${language}/${Paths.PROJECT}/${Paths.OVERVIEW}`);
    }

    private getAcceptInvitationDto(passwordData: ISetOrResetPasswordRequest): AcceptInvitationDto {
        const { firstName, lastName, locale } = this.personalInformationData;
        const { acceptPolicy, subscribeMarketing } = passwordData;
        let acceptInvitationDto: AcceptInvitationDto = {
            firstName,
            lastName,
            locale,
            acceptPolicy,
            subscribeMarketing,
        };

        if (this.organizationName == undefined || this.organizationName.trim() == '') {
            acceptInvitationDto.organizationName = this.personalInformationData.organizationName;
        } else {
            return acceptInvitationDto;
        }

        if (acceptInvitationDto.organizationName == undefined || acceptInvitationDto.organizationName.trim() == '') {
            throw new TypeError('Invalid Organization Name');
        }
        return acceptInvitationDto;
    }

    private checkIfShareExpired(invitation: GetInvitationDto): boolean {
        return invitation.hasShare && !invitation.shareExists;
    }
}
